import React from 'react';
import { Clock, Euro } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ChantierIcon } from '@/components/chantiers/ChantierIcon';
import { cn } from '@/lib/utils';
import { getHoursForDay, formatHoursForDay } from '@/lib/heures-utils';
import { getSpecialiteColor, type CalendarAffectation } from '@/types/calendar';

// Type pour les affectations groupées par chantier
interface GroupedChantier {
  chantierId: string;
  chantierNom: string;
  chantierClient?: string;
  chantierCouleur?: string;
  chantierPictogramme?: string;
  affectations: CalendarAffectation[];
  totalHeures: number;
  totalCout: number;
}

interface ChantierTimelineRowProps {
  chantierGroup: GroupedChantier;
  selectedDate: Date;
  onEdit?: (affectation: CalendarAffectation) => void;
  onDelete?: (id: string) => void;
}

/**
 * Ligne de timeline pour un chantier
 * Affiche le chantier avec des blocs horizontaux pour chaque ouvrier
 */
const ChantierTimelineRow = ({
  chantierGroup,
  selectedDate,
  onEdit,
  onDelete,
}: ChantierTimelineRowProps) => {
  const chantierCouleur = chantierGroup.chantierCouleur || '#6366f1';

  return (
    <div className="flex gap-4 group">
      {/* ═══ COLONNE ESPACEMENT ═══ */}
      <div className="w-14 flex-shrink-0" />

      {/* ═══ TIMELINE ═══ */}
      <div className="flex flex-col items-center">
        {/* Point coloré */}
        <div
          className="w-3 h-3 rounded-full ring-2 ring-background"
          style={{ backgroundColor: chantierCouleur }}
        />
        {/* Ligne verticale */}
        <div
          className="w-0.5 flex-1 min-h-[60px] opacity-30"
          style={{ backgroundColor: chantierCouleur }}
        />
      </div>

      {/* ═══ CARTE CONTENU ═══ */}
      <div
        className={cn(
          'flex-1 rounded-xl border p-4 mb-4',
          'bg-card/50 backdrop-blur-sm',
          'transition-all duration-200',
          'hover:shadow-lg hover:border-border'
        )}
        style={{
          borderColor: `${chantierCouleur}40`,
          backgroundColor: `${chantierCouleur}10`,
        }}
      >
        {/* Header : Icône + Nom chantier + Stats totaux */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <ChantierIcon
              pictogramme={chantierGroup.chantierPictogramme || 'Home'}
              couleur={chantierCouleur}
              size="md"
              showBackground={true}
            />
            <div className="min-w-0">
              <span className="font-semibold text-foreground truncate block">
                {chantierGroup.chantierNom}
              </span>
              {chantierGroup.chantierClient && (
                <span className="text-xs text-foreground/80 font-medium truncate block">
                  {chantierGroup.chantierClient}
                </span>
              )}
            </div>
          </div>

          {/* Stats totaux du chantier */}
          <div className="flex items-center gap-3 text-xs text-foreground font-medium flex-shrink-0">
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{chantierGroup.totalHeures}h</span>
            </div>
            <div className="flex items-center gap-1">
              <Euro className="w-3.5 h-3.5" />
              <span>{chantierGroup.totalCout.toFixed(0)}€</span>
            </div>
          </div>
        </div>

        {/* ═══ BLOCS OUVRIERS HORIZONTAUX ═══ */}
        <div className="flex flex-wrap gap-2">
          {chantierGroup.affectations.map((aff) => {
            const specialite = aff.membre?.specialite || aff.membre?.poste || null;
            const colors = getSpecialiteColor(specialite);
            // Utiliser getHoursForDay pour les heures précises (demi-journée ou journée complète)
            const heuresJour = getHoursForDay(aff, selectedDate);
            const tauxHoraire = aff.taux_horaire_specifique ?? aff.membre?.taux_horaire ?? 0;
            const coef = aff.membre?.coefficient_charges || 1.85;
            const coutJournalier = heuresJour * tauxHoraire * (coef || 1.85);

            return (
              <div
                key={aff.id}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg',
                  'border transition-all duration-200',
                  'hover:shadow-md cursor-pointer',
                  colors.bg,
                  colors.border
                )}
                onClick={() => onEdit?.(aff)}
              >
                {/* Avatar initiales */}
                <div className="w-7 h-7 rounded-full bg-background/80 flex items-center justify-center text-xs font-bold">
                  {aff.membre?.prenom?.[0]}{aff.membre?.nom?.[0]}
                </div>

                {/* Info ouvrier */}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {aff.membre?.prenom} {aff.membre?.nom?.[0]}.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-foreground/80 font-medium">
                    <span>{formatHoursForDay(aff, selectedDate)}</span>
                    <span>•</span>
                    <span>{coutJournalier.toFixed(0)}€</span>
                  </div>
                </div>

                {/* Badge spécialité */}
                {specialite && (
                  <Badge
                    variant="outline"
                    className={cn('text-[10px] px-1.5 py-0 h-5 flex-shrink-0', colors.text, colors.border)}
                  >
                    {specialite}
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChantierTimelineRow;
